import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { useGetAllUsers, useUpdateManyUsers } from '@/hooks/user.hooks';
import { useNavigate } from 'react-router-dom';
import { UserTableView } from '../components/UserTableView';
import { TUserSchema } from '@/schemas/user/user.schema';
import { UserTableHeaders } from '../components/UserTableHeaders';
import { UserTableData } from '../components/UserTableData';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { WarningMessage } from '@/components/Message/WarningMessage';
import { useUserRole } from '@/hooks/auth.hooks';

export const UserListPage = () => {
  const { t } = useTranslation('user');
  const navigate = useNavigate();
  const [totalActive, setTotalActive] = useState<number>(0);

  const { hasNoRole, isModerator } = useUserRole();
  const canManageActions = hasNoRole || isModerator;

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TUserSchema>({
    t,
    linkedFieldNames: ['entityRoles'],
    useUpdateData: useUpdateManyUsers,
  });
  /**
   * === END OF Deactivate Data section ===
   */

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  return (
    <>
      <PageHeader
        title={t('common:users')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListElement={
          canManageActions ? (
            <></>
          ) : (
            <>
              <HeadingButton
                text={t('common:importAList')}
                leftIcon={
                  <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                }
                onClick={() => navigate('#')}
              />
              <HeadingButton
                text={t('addUser')}
                leftIcon={
                  <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
                }
                onClick={() => navigate('/user/create')}
              />
            </>
          )
        }
      />
      <div>
        <UserTableView<TUserSchema>
          t={t}
          useGetAllData={useGetAllUsers}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={UserTableHeaders}
          tableData={UserTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage message={t('common:unableToDeactivateLinkedItem')} />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans
              i18nKey="common:confirmDeactivateItems"
              components={{ br: <br /> }}
            />
          ) : (
            t('common:confirmDeactivateItem')
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
    </>
  );
};
